import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Card, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useShopSystemConfiguration } from 'src/Libs/shop/hooks'
import { SystemConfigurationComponentsResponse } from 'src/Libs/shop/models'
import { PowerMeterQuestionTypes } from 'src/Libs/shop/types'
import { useTypedSelector } from 'src/State/RootReducer'
import { makeStyles } from 'tss-react/mui'
import { ShopMainSkeleton } from '../../Skeletons'

interface PowerMeterTypeProps {
  farmingLength: number
  houseConnectionLength: number
  externalSourceLength: number
  selectedQuestion: string
  onSelectedQuestionChange: (newPowerMeterType: PowerMeterQuestionTypes) => void
}

const useStyles = makeStyles()(theme => ({
  optionContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(3),
  },
  options: {
    position: 'relative',
    paddingTop: theme.spacing(3),
    boxShadow: 'none',
    border: `1px solid ${theme.palette.grey.grey06}`,
    marginTop: theme.spacing(2),

    '&:hover': {
      cursor: 'pointer',
    },
  },
  optionsActive: {
    border: `1px solid ${theme.palette.primary.main}`,
  },
  selected: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    padding: `${theme.spacing(0)} ${theme.spacing(2)}`,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.primary.contrastText,
  },

  disabled: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    backgroundColor: theme.palette.grey.grey06,
    padding: `${theme.spacing(0)} ${theme.spacing(2)}`,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.primary.contrastText,
  },
  optionTextContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
}))
const ExternalSourceMaxLength = 4

const PowerMeterType: React.FC<PowerMeterTypeProps> = ({
  farmingLength,
  houseConnectionLength,
  externalSourceLength,
  selectedQuestion,
  onSelectedQuestionChange,
}) => {
  const { t } = useCustomTranslation()
  const { getSystemConfigurationComponents } = useShopSystemConfiguration()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [configuration, setConfiguration] = useState<SystemConfigurationComponentsResponse | null>(null)
  const configuratorItems = useTypedSelector(state => state.systemStorage.items)
  const selectedElement = configuratorItems.length > 0 ? configuratorItems[0].configuration.configuratorId : null
  const { classes, cx } = useStyles()

  useEffect(() => {
    setIsLoading(true)
    if (selectedElement) {
      getSystemConfigurationComponents(selectedElement)
        .then(result => {
          setConfiguration(result)
        })
        .catch(error => {
          console.error(error)
        })
        .finally(() => setIsLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isLoading) {
    return <ShopMainSkeleton shopSkeletonType="product" />
  }

  return (
    <Box className={classes.optionContainer}>
      {configuration?.powerMeterExternal && (
        <Card
          className={cx(classes.options, selectedQuestion === PowerMeterQuestionTypes.ExternalSource && classes.optionsActive)}
          onClick={() => externalSourceLength < ExternalSourceMaxLength && onSelectedQuestionChange(PowerMeterQuestionTypes.ExternalSource)}
        >
          {selectedQuestion === PowerMeterQuestionTypes.ExternalSource && (
            <Typography className={classes.selected}>{t('shop.common.selected')}</Typography>
          )}
          {externalSourceLength >= ExternalSourceMaxLength && <Typography className={classes.disabled}>{t('shop.common.not-selectable')}</Typography>}
          <Typography variant="h3">{t('shop.power-meter.externe-quelle')}</Typography>
          <Box className={classes.optionTextContainer}>
            <Typography>{t('shop.power-meter.externe-quelle-description')}</Typography>
          </Box>
        </Card>
      )}
      {configuration?.powerMeterFarming && (
        <Card
          className={cx(classes.options, selectedQuestion === PowerMeterQuestionTypes.Farming && classes.optionsActive)}
          onClick={() => farmingLength === 0 && onSelectedQuestionChange(PowerMeterQuestionTypes.Farming)}
        >
          {selectedQuestion === PowerMeterQuestionTypes.Farming && <Typography className={classes.selected}>{t('shop.common.selected')}</Typography>}
          {farmingLength >= 1 && <Typography className={classes.disabled}>{t('shop.common.not-selectable')}</Typography>}
          <Typography variant="h3">{t('shop.power-meter.farming')}</Typography>
          <Box className={classes.optionTextContainer}>
            <Typography>{t('shop.power-meter.farming-description')}</Typography>
          </Box>
        </Card>
      )}
      {configuration?.powerMeterHouseConnection && (
        <Card
          className={cx(classes.options, selectedQuestion === PowerMeterQuestionTypes.HouseConnection && classes.optionsActive)}
          onClick={() => houseConnectionLength === 0 && onSelectedQuestionChange(PowerMeterQuestionTypes.HouseConnection)}
        >
          {selectedQuestion === PowerMeterQuestionTypes.HouseConnection && (
            <Typography className={classes.selected}>{t('shop.common.selected')}</Typography>
          )}
          {houseConnectionLength >= 1 && <Typography className={classes.disabled}>{t('shop.common.not-selectable')}</Typography>}
          <Typography variant="h3">{t('shop.power-meter.house-connection')}</Typography>
          <Box className={classes.optionTextContainer}>
            <Typography>{t('shop.power-meter.house-connection-description')}</Typography>
          </Box>
        </Card>
      )}
    </Box>
  )
}

export default PowerMeterType
